/**
 * Local persistence. Everything lives in this browser's IndexedDB: the
 * workspace object under one key, each uploaded file under its own key.
 * Nothing is sent anywhere.
 *
 * If IndexedDB is unavailable (some private-browsing modes), the store falls
 * back to memory and reports that, so the UI can say progress won't be kept
 * rather than silently losing it.
 */

import { createStore, del, get, keys, set, type UseStore } from 'idb-keyval';
import { migrate } from './migrations.js';
import type { Workspace } from './schema.js';

const WS_KEY = 'workspace';
const DOC_PREFIX = 'doc:';

let idb: UseStore | null = null;
const memory = new Map<string, unknown>();
let persistent: boolean | null = null;

async function db(): Promise<UseStore | null> {
  if (persistent === false) return null;
  if (idb) return idb;
  try {
    if (typeof indexedDB === 'undefined') throw new Error('no indexedDB');
    const s = createStore('tyche-business', 'kv');
    await set('__probe', 1, s);
    await del('__probe', s);
    idb = s;
    persistent = true;
    return s;
  } catch {
    persistent = false;
    return null;
  }
}

/** True once storage has been probed and is persistent. */
export async function isPersistent(): Promise<boolean> {
  await db();
  return persistent === true;
}

async function put(key: string, value: unknown): Promise<void> {
  const s = await db();
  if (s) await set(key, value, s);
  else memory.set(key, value);
}

async function read<T>(key: string): Promise<T | undefined> {
  const s = await db();
  return s ? get<T>(key, s) : (memory.get(key) as T | undefined);
}

async function remove(key: string): Promise<void> {
  const s = await db();
  if (s) await del(key, s);
  else memory.delete(key);
}

export async function loadWorkspace(): Promise<Workspace | null> {
  const raw = await read<unknown>(WS_KEY);
  if (raw === undefined) return null;
  return migrate(raw);
}

export async function saveWorkspace(ws: Workspace): Promise<void> {
  await put(WS_KEY, ws);
}

export async function saveDocBlob(id: string, blob: Blob): Promise<void> {
  await put(DOC_PREFIX + id, blob);
}

export async function getDocBlob(id: string): Promise<Blob | undefined> {
  return read<Blob>(DOC_PREFIX + id);
}

export async function deleteDocBlob(id: string): Promise<void> {
  await remove(DOC_PREFIX + id);
}

/** Delete everything this app has stored on this device. */
export async function clearEverything(): Promise<void> {
  const s = await db();
  if (s) {
    for (const k of await keys(s)) await del(k, s);
  }
  memory.clear();
}
